/**
 * B2B-Bestaetigungs-Gate fuer den Checkout (Paket B Umsatzsteuer).
 *
 * BGH-/LG-Trier-Massstab: ein B2B-only-Angebot braucht eine AKTIVE, nicht
 * vorausgewaehlte Unternehmer-Bestaetigung VOR dem Bezahlvorgang. Das Gate
 * sitzt zentral am Anfang von `handleUpgrade` (lib/checkout.ts), damit ALLE
 * Upgrade-Einstiege (Pricing, UpgradeModal, UpgradeBanner, UploadSuccessDialog,
 * PostFetchRecommendation, SuccessScoreboard) denselben Dialog durchlaufen.
 *
 * Kleiner Pub/Sub ohne externe Lib: der Host (`B2BConfirmHost` in
 * components/checkout/B2BConfirmDialog.tsx, gemountet in Layout) registriert
 * sich hier; `requestB2BConfirmation()` reicht ihm einen Resolver und der
 * Dialog entscheidet.
 */

export type B2BConfirmResolver = (confirmed: boolean) => void;

/** Was gerade gebucht wird (A7-NEU-2). Der Dialog macht daraus die Kaufzeile. */
export interface B2BConfirmSubject {
  lookupKey: string;
  currency: 'eur' | 'usd';
}

type HostListener = (resolve: B2BConfirmResolver, subject?: B2BConfirmSubject) => void;

let hostListener: HostListener | null = null;

/**
 * Registriert den (einen) Dialog-Host. Gibt die Unregister-Funktion zurueck;
 * sie raeumt nur auf, wenn der Host noch der eigene ist — ein spaeter
 * gemounteter Ersatz-Host wird nicht mit abgeraeumt.
 */
export function registerB2BConfirmHost(listener: HostListener): () => void {
  hostListener = listener;
  return () => {
    if (hostListener === listener) hostListener = null;
  };
}

/**
 * Fragt die Unternehmer-Bestaetigung an. Aufloesung:
 *  - true  → Nutzer hat die Checkbox gesetzt und bestaetigt.
 *  - false → Abbruch (Cancel/X/Overlay) ODER es ist gar kein Host gemountet.
 *
 * Fail-closed by design: ohne Host wird NICHT durchgewunken — lieber ein
 * Checkout, der nicht startet, als einer ohne Bestaetigung.
 *
 * Das optionale Subject nennt dem Dialog, was gebucht wird; es aendert nichts
 * an der Bestaetigungslogik.
 */
export function requestB2BConfirmation(subject?: B2BConfirmSubject): Promise<boolean> {
  const listener = hostListener;
  if (!listener) {
    return Promise.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    let settled = false;
    listener((confirmed) => {
      // Ein Resolver darf nur einmal wirken (z.B. X-Button + onOpenChange
      // koennen denselben Abbruch doppelt melden).
      if (settled) return;
      settled = true;
      resolve(confirmed);
    }, subject);
  });
}
